import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import WheelPickerSheet from './WheelPickerSheet';
import type { WheelPickerOption, WheelPickerValue } from '../ui/wheel-picker';
import { cn } from '../../lib/utils';

interface WheelPickerFieldProps<T extends WheelPickerValue> {
  /** Names the value on the trigger and heads the sheet. */
  label: string;
  /** What the trigger reads while nothing has been picked. */
  placeholder?: string;
  value: T | null;
  options: WheelPickerOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  /**
   * Mark it as an answer the form is still waiting for. The pill goes red and
   * says so to a screen reader — there is no room under it for a message, and
   * the placeholder already names what is wanted.
   */
  invalid?: boolean;
  className?: string;
  testId?: string;
  /** Rendered under the wheel, for what the sheet decides beyond the value. */
  extra?: ReactNode;
  /**
   * Fired as the sheet opens, so anything in `extra` can be resynced with what
   * was actually saved — a switch flipped and then dismissed has decided
   * nothing, just as the wheel it sits under has.
   */
  onOpen?: () => void;
}

/**
 * A value picked off an iOS-style wheel in a bottom sheet, behind a pill that
 * reads back what is on it.
 */
export default function WheelPickerField<T extends WheelPickerValue>({
  label,
  placeholder,
  value,
  options,
  onChange,
  disabled,
  invalid = false,
  className,
  testId,
  extra,
  onOpen,
}: WheelPickerFieldProps<T>) {
  const [open, setOpen] = useState(false);

  const selected = options.find((option) => option.value === value);

  const openSheet = () => {
    onOpen?.();
    setOpen(true);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={openSheet}
        disabled={disabled || options.length === 0}
        aria-label={label}
        aria-invalid={invalid || undefined}
        data-testid={testId}
        className={cn(
          'h-8 gap-1.5 rounded-full px-3 font-bold',
          !selected && 'font-normal text-muted-foreground',
          invalid && 'border-red-500 text-red-500',
          className
        )}
      >
        {selected ? selected.label : (placeholder ?? label)}
        <ChevronDown className="size-4 opacity-50" aria-hidden />
      </Button>

      <WheelPickerSheet
        open={open}
        onOpenChange={setOpen}
        label={label}
        value={value}
        options={options}
        onChange={onChange}
        extra={extra}
      />
    </>
  );
}
